import React, { Suspense } from 'react';
import { BrowserRouter as Router, Routes, Route } from 'react-router-dom';
import { useAuth } from './hooks/AuthContext';
import { AdminGuard } from './components/AdminGuard';
import { AdminLayout } from './components/admin/AdminLayout';
import { LoadingSpinner } from './components/LoadingSpinner';
import { RequireUser } from './components/RequireUser';
import { ScrollToTop } from './components/ScrollToTop';
import { NotFound } from './pages/NotFound';

// Pages
import { AuthPage } from './pages/auth/AuthPage';
import { PasswordReset } from './pages/auth/PasswordReset';
import { AdminAuth } from './pages/auth/AdminAuth';

// Lazy load heavy admin pages
const AdminDashboard = React.lazy(() => import('./pages/admin/AdminDashboard'));
const ModulesManager = React.lazy(() => import('./pages/admin/ModulesManager'));
const ModuleEditor = React.lazy(() => import('./pages/admin/ModuleEditor'));
const EnhancedModuleEditor = React.lazy(() => import('./pages/admin/EnhancedModuleEditor'));
const Analytics = React.lazy(() => import('./pages/admin/Analytics'));
const Moderation = React.lazy(() => import('./pages/admin/Moderation'));
const Settings = React.lazy(() => import('./pages/admin/Settings'));
const AccessCodes = React.lazy(() => import('./pages/admin/AccessCodes'));
const MeditationCategories = React.lazy(() => import('./pages/admin/MeditationCategories'));

// User Pages
import { UserLayout } from './components/user/UserLayout';
import { ReliableDashboard } from './pages/user/ReliableDashboard';
import { Journal } from './pages/user/Journal';
import { JournalEditor } from './pages/user/JournalEditor';
import { PromptedJournalNew as PromptedJournal } from './pages/user/PromptedJournalNew';
import { MorningJournalNew as MorningJournal } from './pages/user/MorningJournalNew';
import { EveningJournalNew as EveningJournal } from './pages/user/EveningJournalNew';
import { JournalResults } from './pages/user/JournalResults';
import { TrainingHub } from './pages/user/TrainingHub';
import { ModulePlayer } from './pages/user/ModulePlayer';
import { TrainingAccess } from './pages/user/TrainingAccess';
import { Meditations } from './pages/user/Meditations';
import { Community } from './pages/user/Community';
import { CommunityMinimal } from './pages/user/CommunityMinimal';
import { CommunitySimple } from './pages/user/CommunitySimple';
import { GroupCreator } from './pages/user/GroupCreator';
import { GroupView } from './pages/user/GroupView';
import { ProfileManager } from './pages/user/ProfileManager';
import { FirebaseTest } from './pages/user/FirebaseTest';
import { PermissionTest } from './pages/user/PermissionTest';
import { CommunityDebug } from './pages/user/CommunityDebug';
import { CommunityTest } from './pages/user/CommunityTest';
import { GroupViewTest } from './pages/user/GroupViewTest';
import GRIPS from './pages/user/TenFour';
import { QuizPage } from './pages/user/QuizPage';
import { Achievements } from './pages/user/Achievements';
import JournalNotifications from './pages/user/JournalNotifications';
import { Support } from './pages/user/Support';
import { PostIncidentCheckin } from './pages/user/PostIncidentCheckin';

// Legal Pages
import { TermsOfService } from './components/legal/TermsOfService';
import { PrivacyPolicy } from './components/legal/PrivacyPolicy';


function App() {
  const { loading } = useAuth();

  if (loading) {
    return (
      <div className="min-h-screen flex items-center justify-center">
        <LoadingSpinner size="lg" />
      </div>
    );
  }

  return (
    <Router>
      <ScrollToTop />
      <Routes>
        {/* User Auth */}
        <Route path="/auth" element={<AuthPage />} />
        <Route path="/auth/action" element={<PasswordReset />} />
        
        {/* Legal Pages - Public Access */}
        <Route path="/terms" element={<TermsOfService />} />
        <Route path="/privacy" element={<PrivacyPolicy />} />
        
        {/* Admin Auth */}
        <Route path="/admin/auth" element={<AdminAuth />} />
        
        {/* Main User App */}
        <Route 
          path="/" 
          element={
            <RequireUser>
              <UserLayout />
            </RequireUser>
          } 
        >
          <Route index element={<ReliableDashboard />} />
          <Route path="journal" element={<PromptedJournal />} />
          <Route path="journal/morning" element={<MorningJournal />} />
          <Route path="journal/morning/results" element={<JournalResults />} />
          <Route path="journal/evening" element={<EveningJournal />} />
          <Route path="journal/evening/results" element={<JournalResults />} />
          <Route path="journal/history" element={<Journal />} />
          <Route path="journal/notifications" element={<JournalNotifications />} />
          <Route path="journal/prompted/:id" element={<JournalResults />} />
          <Route path="journal/:id" element={<JournalEditor />} />
          <Route path="journal/:id/edit" element={<JournalEditor />} />
          <Route path="training" element={<TrainingHub />} />
          <Route path="training/:id" element={<ModulePlayer />} />
          <Route path="meditations" element={<Meditations />} />
          <Route path="meditations/:id" element={<ModulePlayer />} />
          <Route path="training-access" element={<TrainingAccess />} />
          <Route path="achievements" element={<Achievements />} />
          <Route path="community" element={<Community />} />
          <Route path="community-simple" element={<CommunitySimple />} />
          <Route path="community-full" element={<Community />} />
          <Route path="community-minimal" element={<CommunityMinimal />} />
          <Route path="community-direct" element={<Community />} />
          <Route path="community-test" element={<CommunityTest />} />
          <Route path="community/create-group" element={<GroupCreator />} />
          <Route path="community/groups/:id" element={<GroupView />} />
          <Route path="community/groups/:id/test" element={<GroupViewTest />} />
          <Route path="profile/public" element={<ProfileManager mode="public" />} />
          <Route path="profile/privacy" element={<ProfileManager mode="privacy" />} />
          <Route path="tenfour" element={<GRIPS />} />
          <Route path="grips" element={<GRIPS />} />
          <Route path="incident-checkin" element={<PostIncidentCheckin />} />
          <Route path="quiz/:quizId" element={<QuizPage />} />
          <Route path="support" element={<Support />} />
                <Route path="firebase-test" element={<FirebaseTest />} />
                <Route path="permission-test" element={<PermissionTest />} />
                <Route path="community-debug" element={<CommunityDebug />} />
        </Route>
        
        {/* Admin Routes */}
        <Route 
          path="/admin" 
          element={
            <AdminGuard requiredRoles={['admin', 'content_editor', 'analyst']}>
              <Suspense fallback={<LoadingSpinner size="lg" />}>
                <AdminLayout />
              </Suspense>
            </AdminGuard>
          }
        >
          <Route index element={<AdminDashboard />} />
          <Route 
            path="modules" 
            element={
              <AdminGuard requiredRoles={['admin', 'content_editor']}>
                <ModulesManager />
              </AdminGuard>
            } 
          />
          <Route 
            path="modules/:id" 
            element={
              <AdminGuard requiredRoles={['admin', 'content_editor']}>
                <ModuleEditor />
              </AdminGuard>
            } 
          />
          <Route 
            path="modules/:id/enhanced" 
            element={
              <AdminGuard requiredRoles={['admin', 'content_editor']}>
                <EnhancedModuleEditor />
              </AdminGuard>
            } 
          />
          <Route 
            path="modules/new" 
            element={
              <AdminGuard requiredRoles={['admin', 'content_editor']}>
                <EnhancedModuleEditor />
              </AdminGuard>
            } 
          />
          <Route 
            path="modules/new/enhanced" 
            element={
              <AdminGuard requiredRoles={['admin', 'content_editor']}>
                <EnhancedModuleEditor />
              </AdminGuard>
            } 
          />
          <Route 
            path="meditation-categories" 
            element={
              <AdminGuard requiredRoles={['admin', 'content_editor']}>
                <MeditationCategories />
              </AdminGuard>
            } 
          />
          <Route 
            path="analytics" 
            element={
              <AdminGuard requiredRoles={['admin', 'analyst']}>
                <Analytics />
              </AdminGuard>
            } 
          />
          <Route 
            path="moderation" 
            element={
              <AdminGuard requiredRoles={['admin']}>
                <Moderation />
              </AdminGuard>
            } 
          />
          <Route 
            path="access-codes" 
            element={
              <AdminGuard requiredRoles={['admin']}>
                <AccessCodes />
              </AdminGuard>
            } 
          />
          <Route 
            path="settings" 
            element={
              <AdminGuard requiredRoles={['admin']}>
                <Settings />
              </AdminGuard>
            } 
          />
        </Route>
        
        {/* 404 Not Found */}
        <Route path="*" element={<NotFound />} />
      </Routes>
    </Router>
  );
}

export default App;